import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { format } from 'date-fns';
import { Subject, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs';
import { PayeeCasesService } from '../payee-cases.service';
import { ChipDropdownComponent } from '../chip-dropdown/chip-dropdown.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDatepickerComponent } from '../../payee-form-fields/payee-datepicker/payee-datepicker.component';
import { PayeeMiniIconButtonComponent } from '../../payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'payee-case-transition-filter',
  templateUrl: './case-transition-filter.component.html',
  styleUrls: ['./case-transition-filter.component.scss'],
  imports: [
    ChipDropdownComponent,
    PayeeFormFieldComponent,
    PayeeDatepickerComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
    PayeeButtonComponent,
    TranslateModule,
  ],
})
export class CaseTransitionFilterComponent implements OnInit, OnDestroy {
  private payeeCasesService = inject(PayeeCasesService);
  private fb = inject(FormBuilder);

  private readonly destroy = new Subject<void>();

  readonly form = this.fb.group({
    from: this.fb.control<string | null>(null),
    to: this.fb.control<string | null>(null),
  });

  readonly max = new Date();

  get fromControl() {
    return this.form.controls.from;
  }
  get toControl() {
    return this.form.controls.to;
  }

  get selected(): boolean {
    return (
      !!this.payeeCasesService.transition_at_min.value ||
      !!this.payeeCasesService.transition_at_max.value
    );
  }

  dropdownOpen = false;

  ngOnInit(): void {
    this.payeeCasesService.transition_at_min
      .pipe(
        takeUntil(this.destroy),
        startWith(this.payeeCasesService.transition_at_min.value),
        map((v) => (v instanceof Date ? format(v, 'yyyy-MM-dd') : v)),
        distinctUntilChanged(),
      )
      .subscribe((v) => {
        this.fromControl.patchValue(v, { emitEvent: false });
      });

    this.payeeCasesService.transition_at_max
      .pipe(
        takeUntil(this.destroy),
        startWith(this.payeeCasesService.transition_at_max.value),
        map((v) => (v instanceof Date ? format(v, 'yyyy-MM-dd') : v)),
        distinctUntilChanged(),
      )
      .subscribe((v) => {
        this.toControl.patchValue(v, { emitEvent: false });
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  applyFilter(): void {
    this.payeeCasesService.transition_at_min.next(this.fromControl.value);
    this.payeeCasesService.transition_at_max.next(this.toControl.value);
    this.dropdownOpen = false;
  }

  clearFilter(): void {
    this.fromControl.patchValue(null);
    this.toControl.patchValue(null);
    this.payeeCasesService.transition_at_min.next(null);
    this.payeeCasesService.transition_at_max.next(null);
    this.dropdownOpen = false;
  }

  readonly displayWith = (): string => {
    const { transitionAtMin, transitionAtMax } =
      this.payeeCasesService.getFilters();

    return `${transitionAtMin ?? ''} - ${transitionAtMax ?? ''}`.trim();
  };
}
